import React, { useEffect } from "react";
import { Box, Typography, Stack, Chip, Button, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { BsArrowLeft, BsFilePdf } from "react-icons/bs";
import {
  TbTopologyStar3, TbArrowsExchange, TbReplace, TbShieldLock, TbBolt, TbStack2, TbApi,
} from "react-icons/tb";
import { colors, font, pagePx } from "../theme/tokens";
import Reveal from "../components/Reveal";
import {
  MicroservicesDiagram, KafkaCqrsDiagram, ModelSelectorDiagram,
  CircuitBreakerDiagram, StateMachineDiagram,
} from "../components/diagrams/Diagrams";

/* ---------- small building blocks ---------- */

const Eyebrow = ({ children }) => (
  <Typography sx={{ fontFamily: font.mono, fontSize: 13, fontWeight: 600, letterSpacing: "3px", textTransform: "uppercase", color: colors.accent, mb: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
    <Box component="span" sx={{ width: 26, height: 2, background: colors.gradPrimary }} /> {children}
  </Typography>
);

const H2 = ({ children }) => (
  <Typography variant="h2" sx={{ fontFamily: font.sans, fontWeight: 700, fontSize: { xs: 24, md: 32 }, color: colors.ink, mb: 2, lineHeight: 1.2 }}>
    {children}
  </Typography>
);

const P = ({ children, sx }) => (
  <Typography sx={{ fontSize: { xs: 15, md: 16 }, color: colors.inkSoft, lineHeight: 1.8, mb: 2.5, ...sx }}>
    {children}
  </Typography>
);

const Strong = ({ children }) => <Box component="span" sx={{ color: colors.ink, fontWeight: 600 }}>{children}</Box>;

const Section = ({ children, alt, sx }) => (
  <Box component="section" sx={{ px: pagePx, py: { xs: 6, md: 9 }, backgroundColor: alt ? colors.bgAlt : "transparent", ...sx }}>
    <Box sx={{ maxWidth: 1080, mx: "auto" }}>{children}</Box>
  </Box>
);

const DiagramCard = ({ title, caption, children }) => (
  <Box sx={{ borderRadius: "18px", border: `1px solid ${colors.border}`, backgroundColor: colors.surface, p: { xs: 2, md: 3.5 }, my: 3 }}>
    {title && <Typography sx={{ fontFamily: font.sans, fontWeight: 600, fontSize: 15, color: colors.ink, mb: 2.5 }}>{title}</Typography>}
    {children}
    {caption && <Typography sx={{ fontSize: 12.5, color: colors.inkMute, fontFamily: font.mono, mt: 2, textAlign: "center" }}>{caption}</Typography>}
  </Box>
);

const patterns = [
  { icon: <TbReplace />, name: "Registry + Strategy", text: "VTO providers registered and selected at runtime from DB config — swap the inference model with zero code change." },
  { icon: <TbStack2 />, name: "Dependency Injection (IoC)", text: "All VTOService dependencies injected via FastAPI Depends — every one mockable in unit tests." },
  { icon: <TbArrowsExchange />, name: "CQRS", text: "Reads separated from writes; queries served from Kafka-synced local read-models with Redis cache." },
  { icon: <TbBolt />, name: "Circuit Breaker", text: "Outbound calls to AI providers and storage protected from cascading failure with fail-fast breakers." },
  { icon: <TbTopologyStar3 />, name: "Observer", text: "Try-on completion fans out to decoupled observers (notifications, audit log) without touching core logic." },
  { icon: <TbApi />, name: "Factory", text: "Image-generation providers instantiated through a factory, with a mock variant for testing." },
];

const stack = [
  { group: "Backend (AI)", items: "FastAPI · Celery" },
  { group: "Backend (business)", items: "NestJS (TypeScript)" },
  { group: "Data", items: "PostgreSQL ×5 · Redis · MinIO (S3)" },
  { group: "Messaging", items: "Apache Kafka" },
  { group: "AI / VTO", items: "fal.ai · 6 image-generation providers" },
  { group: "Observability", items: "Loki · Promtail · Grafana" },
  { group: "Frontend", items: "React · Vite · Redux Toolkit · i18n" },
  { group: "Infra", items: "Docker Compose" },
];

const shots = [
  { src: "/assets/vf_tryon.jpg", label: "AI virtual try-on" },
  { src: "/assets/vf_models.jpg", label: "AI model catalogue" },
  { src: "/assets/vf_bulk_result.jpg", label: "Layered bulk try-on result" },
  { src: "/assets/vf_garments.jpg", label: "Garment wardrobe" },
  { src: "/assets/vf_history.jpg", label: "Result detail & history" },
  { src: "/assets/vf_apidocs.jpg", label: "B2B API documentation" },
];

const VirtuFit = () => {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <Box>
      {/* ===== Hero ===== */}
      <Box sx={{ position: "relative", px: pagePx, pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 8 }, overflow: "hidden" }}>
        <Box className="aurora" sx={{ width: 460, height: 460, background: "#4f46e5", top: -160, right: -80, opacity: 0.4 }} />
        <Box className="dot-grid" sx={{ position: "absolute", inset: 0, opacity: 0.4, maskImage: "radial-gradient(ellipse at 60% 0%, black, transparent 70%)" }} />
        <Box sx={{ maxWidth: 1080, mx: "auto", position: "relative", zIndex: 1 }}>
          <Button
            startIcon={<BsArrowLeft />}
            onClick={() => navigate("/")}
            sx={{ color: colors.inkSoft, textTransform: "none", mb: 4, "&:hover": { color: colors.accent } }}
          >
            Back to portfolio
          </Button>

          <Stack direction="row" spacing={1} sx={{ mb: 2.5, flexWrap: "wrap", gap: 1 }}>
            <Chip label="Case Study" size="small" sx={{ background: colors.gradPrimary, color: "#0b1120", fontWeight: 700, fontFamily: font.mono, fontSize: 11 }} />
            <Chip label="Software Architecture" size="small" sx={{ backgroundColor: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}`, fontSize: 11 }} />
            <Chip label="AI / ML" size="small" sx={{ backgroundColor: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}`, fontSize: 11 }} />
          </Stack>

          <Typography variant="h1" sx={{ fontFamily: font.sans, fontWeight: 800, fontSize: { xs: 38, md: 60 }, color: colors.ink, mb: 2, lineHeight: 1.05 }}>
            VirtuFit
          </Typography>
          <Typography sx={{ fontSize: { xs: 17, md: 21 }, color: colors.inkSoft, maxWidth: 720, lineHeight: 1.6, mb: 4 }}>
            An <Strong>AI virtual try-on platform</Strong> architected as eight polyglot microservices — event-driven,
            resilient, and API-first — designed so the AI model behind it can be swapped without touching business logic.
          </Typography>

          <Grid container spacing={2} sx={{ maxWidth: 760, mb: 4 }}>
            {[
              ["Role", "Sole architect & engineer"],
              ["Scope", "Architecture · Backend · AI · Frontend"],
              ["Type", "Final-year capstone (PFE)"],
              ["Timeline", "~6 months"],
            ].map(([k, v]) => (
              <Grid item xs={6} sm={3} key={k}>
                <Typography sx={{ fontFamily: font.mono, fontSize: 11.5, color: colors.inkMute, textTransform: "uppercase", letterSpacing: 1, mb: 0.5 }}>{k}</Typography>
                <Typography sx={{ fontSize: 14, color: colors.ink, fontWeight: 500 }}>{v}</Typography>
              </Grid>
            ))}
          </Grid>

          <Button startIcon={<BsFilePdf />} href="/Rapport_PFE.pdf" target="_blank" rel="noopener noreferrer"
            sx={{ color: colors.ink, fontFamily: font.sans, fontWeight: 600, fontSize: 14.5, textTransform: "none", px: 2.5, py: 1.1, borderRadius: "12px", border: `1px solid ${colors.borderStrong}`, "&:hover": { borderColor: colors.accent, color: colors.accent } }}>
            Read the full report (PDF)
          </Button>
        </Box>
      </Box>

      {/* hero screenshot */}
      <Box sx={{ px: pagePx, mb: { xs: 2, md: 4 } }}>
        <Box sx={{ maxWidth: 1080, mx: "auto" }}>
          <Box component="img" src="/assets/vf_tryon.jpg" alt="VirtuFit try-on interface"
            sx={{ width: "100%", borderRadius: "18px", border: `1px solid ${colors.borderStrong}`, boxShadow: "0 30px 80px rgba(0,0,0,0.55)", display: "block" }} />
        </Box>
      </Box>

      {/* ===== Problem ===== */}
      <Section>
        <Reveal>
          <Eyebrow>The Problem</Eyebrow>
          <H2>Fashion e-commerce can't let you try before you buy</H2>
          <P>
            Online fashion suffers a structural flaw: shoppers can't try garments on. That uncertainty drives
            return rates of <Strong>30–40%</Strong>, heavy logistics cost, and a degraded buying experience.
            <Strong> Virtual try-on (VTO)</Strong> — generating a realistic image of a person wearing a given garment —
            attacks the problem directly.
          </P>
          <P>
            I set out to build a <Strong>generic, extensible VTO platform</Strong> usable both by end users (via a web
            app) and by e-commerce partners (via a B2B API) — one where the AI inference model behind it is a swappable
            detail, not a hard dependency, so the platform can evolve as the model landscape does.
          </P>
        </Reveal>
      </Section>

      {/* ===== Architecture ===== */}
      <Section alt>
        <Reveal>
          <Eyebrow>The Architecture</Eyebrow>
          <H2>Eight polyglot microservices, database-per-service</H2>
          <P>
            Unlike a modular monolith, VTO's workloads are heterogeneous — lightweight CRUD services versus
            heavy AI-inference and orchestration. That justified a <Strong>polyglot microservices architecture</Strong>:
            AI-heavy services in <Strong>Python/FastAPI</Strong>, business services in <Strong>NestJS/TypeScript</Strong>,
            each owning its own database with no shared schema. Design was driven by the <Strong>4+1 view model</Strong> and
            <Strong> ATAM</Strong>, mapping quality attributes (modifiability, scalability, availability) to concrete
            architectural decisions.
          </P>
        </Reveal>
        <Reveal>
          <DiagramCard caption="8 services behind a single API gateway · polyglot · database-per-service">
            <MicroservicesDiagram />
          </DiagramCard>
        </Reveal>

        {/* quality attributes */}
        <Reveal>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {[
              ["Modifiability", "Swap the AI inference model without touching business logic (Strategy + Registry)."],
              ["Scalability", "Independent horizontal scaling of inference services; async processing."],
              ["Availability", "Circuit breakers, provider fallback, health checks, auto-restart."],
              ["Performance", "Redis cache + local CQRS read-models; treated as an ISO/IEC 25010 NFR."],
            ].map(([k, v]) => (
              <Grid item xs={12} sm={6} md={3} key={k}>
                <Box sx={{ height: "100%", p: 2.2, borderRadius: "14px", backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                  <Typography sx={{ fontFamily: font.sans, fontWeight: 600, fontSize: 14.5, color: colors.accent, mb: 0.8 }}>{k}</Typography>
                  <Typography sx={{ fontSize: 13, color: colors.inkSoft, lineHeight: 1.6 }}>{v}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Reveal>
      </Section>

      {/* ===== Event-driven / CQRS ===== */}
      <Section>
        <Reveal>
          <Eyebrow>Event-Driven Communication</Eyebrow>
          <H2>Kafka + CQRS: services that don't call each other on the hot path</H2>
          <P>
            Synchronous requests go through the gateway, but state is kept consistent asynchronously over
            <Strong> Apache Kafka</Strong>. When a mannequin or garment changes, the owning service publishes an
            event (<Box component="span" sx={{ fontFamily: font.mono, color: colors.amber, fontSize: 14 }}>vto.aimodel.*</Box>,
            {" "}<Box component="span" sx={{ fontFamily: font.mono, color: colors.amber, fontSize: 14 }}>vto.garment.*</Box>).
            The VTO service subscribes and <Strong>projects those events into its own local read-models</Strong>.
          </P>
          <P>
            This is <Strong>CQRS</Strong> in practice: on each try-on, the VTO service never makes a synchronous call
            to the mannequin or garment service — it reads its Redis-cached read-model, falling back to HTTP only if an
            event hasn't arrived yet (eventual consistency). The result: a fast hot path that survives downstream outages.
          </P>
        </Reveal>
        <Reveal>
          <DiagramCard caption="Write side publishes → Kafka topics → VTO service projects into local read-models (CQRS)">
            <KafkaCqrsDiagram />
          </DiagramCard>
        </Reveal>
      </Section>

      {/* ===== Design patterns ===== */}
      <Section alt>
        <Reveal>
          <Eyebrow>Engineering Quality</Eyebrow>
          <H2>Design patterns, applied where they earn their keep</H2>
          <P>Seven design patterns run in production code — each solving a real coupling or resilience problem, not decoration.</P>
        </Reveal>
        <Grid container spacing={2}>
          {patterns.map((p, i) => (
            <Grid item xs={12} sm={6} md={4} key={p.name}>
              <Reveal delay={i * 60}>
                <Box sx={{ height: "100%", p: 2.6, borderRadius: "15px", backgroundColor: colors.surface, border: `1px solid ${colors.border}`, transition: "all 0.2s ease", "&:hover": { borderColor: "rgba(99,102,241,0.5)", transform: "translateY(-3px)" } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2, color: colors.primary, "& svg": { fontSize: 22 } }}>
                    {p.icon}
                    <Typography sx={{ fontFamily: font.sans, fontWeight: 600, fontSize: 15.5, color: colors.ink }}>{p.name}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13.5, color: colors.inkSoft, lineHeight: 1.65 }}>{p.text}</Typography>
                </Box>
              </Reveal>
            </Grid>
          ))}
        </Grid>

        <Reveal>
          <Box sx={{ mt: 4 }}>
            <DiagramCard title="Circuit breaker — failing fast to prevent cascading outages">
              <CircuitBreakerDiagram />
            </DiagramCard>
            <DiagramCard title="Result lifecycle — an explicit state machine rejects invalid transitions">
              <StateMachineDiagram />
            </DiagramCard>
          </Box>
        </Reveal>
      </Section>

      {/* ===== Model selector ===== */}
      <Section>
        <Reveal>
          <Eyebrow>AI Model Management</Eyebrow>
          <H2>A hot-swappable model registry with automatic fallback</H2>
          <P>
            The image-generation service unifies <Strong>seven providers</Strong> (Pollinations, OpenAI, Gemini,
            HuggingFace, Replicate, Google Imagen) behind a factory. A <Strong>ModelSelector</Strong> applies a priority
            chain — explicit override → active configured model → highest-priority model in DB — and a
            <Strong> FallbackManager</Strong> transparently rolls over to the next provider on failure, while a
            performance monitor tracks latency and success rate.
          </P>
        </Reveal>
        <Reveal>
          <DiagramCard caption="Factory + ModelSelector + Fallback: providers are swappable without a redeploy">
            <ModelSelectorDiagram />
          </DiagramCard>
        </Reveal>
      </Section>

      {/* ===== B2B API ===== */}
      <Section alt>
        <Reveal>
          <Eyebrow>API-First</Eyebrow>
          <H2>Built for partners, secured in depth</H2>
          <P>
            Beyond the web app, VirtuFit exposes a <Strong>B2B API</Strong> for e-commerce partners. Authentication uses
            API keys (with quotas and usage logs) distinct from user JWTs. The gateway validates the key, applies
            <Strong> Redis-backed rate limiting and quotas</Strong>, routes the request, then logs usage — a layered,
            <Strong> defense-in-depth</Strong> security posture. A dedicated developer role manages keys and reads the docs.
          </P>
          <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", gap: 1.5, mb: 1 }}>
            {["JWT + Google OAuth", "API-key auth", "Rate limiting", "Quotas", "Turnstile", "Usage logging"].map((t) => (
              <Box key={t} sx={{ display: "flex", alignItems: "center", gap: 0.8, fontSize: 13, color: colors.inkSoft, border: `1px solid ${colors.border}`, borderRadius: "9px", px: 1.4, py: 0.7 }}>
                <TbShieldLock style={{ color: colors.green }} /> {t}
              </Box>
            ))}
          </Stack>
        </Reveal>
      </Section>

      {/* ===== Gallery ===== */}
      <Section alt>
        <Reveal>
          <Eyebrow>Product</Eyebrow>
          <H2>The platform in action</H2>
        </Reveal>
        <Grid container spacing={2}>
          {shots.map((s, i) => (
            <Grid item xs={12} sm={6} md={4} key={s.src}>
              <Reveal delay={i * 60}>
                <Box sx={{ borderRadius: "14px", overflow: "hidden", border: `1px solid ${colors.border}`, backgroundColor: colors.surface }}>
                  <Box component="img" src={s.src} alt={s.label} sx={{ width: "100%", display: "block", aspectRatio: "16/10", objectFit: "cover", objectPosition: "top" }} />
                  <Typography sx={{ fontSize: 12.5, color: colors.inkSoft, fontFamily: font.mono, p: 1.4, borderTop: `1px solid ${colors.border}` }}>{s.label}</Typography>
                </Box>
              </Reveal>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* ===== Tech stack ===== */}
      <Section>
        <Reveal>
          <Eyebrow>Tech Stack</Eyebrow>
          <H2>What it's built with</H2>
        </Reveal>
        <Grid container spacing={2}>
          {stack.map((s, i) => (
            <Grid item xs={12} sm={6} md={3} key={s.group}>
              <Reveal delay={i * 50}>
                <Box sx={{ height: "100%", p: 2.2, borderRadius: "13px", backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                  <Typography sx={{ fontFamily: font.mono, fontSize: 11.5, color: colors.inkMute, textTransform: "uppercase", letterSpacing: 0.8, mb: 0.8 }}>{s.group}</Typography>
                  <Typography sx={{ fontSize: 14, color: colors.ink, lineHeight: 1.5 }}>{s.items}</Typography>
                </Box>
              </Reveal>
            </Grid>
          ))}
        </Grid>

        <Reveal>
          <Box sx={{ mt: 5, p: { xs: 3, md: 4 }, borderRadius: "18px", border: "1px solid rgba(99,102,241,0.35)", background: "linear-gradient(135deg, rgba(99,102,241,0.12), rgba(34,211,238,0.05))", textAlign: "center" }}>
            <Typography sx={{ fontFamily: font.sans, fontWeight: 700, fontSize: { xs: 20, md: 24 }, color: colors.ink, mb: 1.5 }}>
              Want the deep technical detail?
            </Typography>
            <Typography sx={{ fontSize: 15, color: colors.inkSoft, mb: 3, maxWidth: 560, mx: "auto" }}>
              Happy to walk through the architecture decisions, trade-offs, and code. Let's talk.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
              <Button href="mailto:hamdaniibrahim.contact@gmail.com"
                sx={{ background: colors.gradPrimary, color: "#0b1120", fontWeight: 700, textTransform: "none", px: 3, py: 1.2, borderRadius: "12px", "&:hover": { transform: "translateY(-2px)" }, transition: "all 0.2s" }}>
                Get in touch
              </Button>
              <Button startIcon={<BsArrowLeft />} onClick={() => navigate("/")}
                sx={{ color: colors.ink, textTransform: "none", px: 3, py: 1.2, borderRadius: "12px", border: `1px solid ${colors.borderStrong}`, "&:hover": { borderColor: colors.accent } }}>
                Back to portfolio
              </Button>
            </Stack>
          </Box>
        </Reveal>
      </Section>
    </Box>
  );
};

export default VirtuFit;
